const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function getTimeRemaining(endTime) {
  const total = endTime - Date.now();

  return {
    total,
    days: Math.floor(total / DAY),
    hours: Math.floor((total / HOUR) % 24),
    minutes: Math.floor((total / MINUTE) % 60),
    seconds: Math.floor((total / SECOND) % 60),
  };
}

/**
 * `2D 5H 22M 30S`, with zero segments dropped from the front only — so a
 * countdown under an hour reads `5M 09S`, but `1D 0H 0M 05S` keeps its zeroes
 * because a larger unit is still running. Seconds are always two digits.
 */
function formatRemaining({ days, hours, minutes, seconds }, strings) {
  const segments = [
    { value: days, unit: strings.days },
    { value: hours, unit: strings.hours },
    { value: minutes, unit: strings.minutes },
  ];

  const firstRunning = segments.findIndex(({ value }) => value > 0);
  const leading = firstRunning === -1 ? [] : segments.slice(firstRunning);

  return [
    ...leading.map(({ value, unit }) => `${value}${unit}`),
    `${String(seconds).padStart(2, "0")}${strings.seconds}`,
  ].join(":");
}

class DateCountdown extends HTMLElement {
  connectedCallback() {
    this.strings = JSON.parse(
      this.querySelector("[data-date-strings]").textContent,
    );
    this.valueEl = this.querySelector("[data-countdown-value]");
    this.endTime = Date.parse(this.getAttribute("end-time"));

    if (Number.isNaN(this.endTime)) {
      this._end();
      return;
    }

    this._tick();
    this.interval = setInterval(() => this._tick(), SECOND);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  _tick() {
    const remaining = getTimeRemaining(this.endTime);

    if (remaining.total <= 0) {
      this._end();
      return;
    }

    const { days, hours, minutes } = remaining;

    this.valueEl.textContent = formatRemaining(remaining, this.strings);

    // The accessible label is only refreshed on the minute — a per-second
    // update would interrupt a screen reader continuously.
    if (minutes !== this._announcedMinute) {
      this._announcedMinute = minutes;
      this.setAttribute(
        "aria-label",
        this.strings.accessible_label
          .replace("[days]", days)
          .replace("[hours]", hours)
          .replace("[minutes]", minutes),
      );
    }
  }

  _end() {
    clearInterval(this.interval);
    this.setAttribute("ended", "");
    this.removeAttribute("aria-label");
    this.dispatchEvent(new CustomEvent("promo-bar:update", { bubbles: true }));
  }
}

if (!customElements.get("date-countdown")) {
  customElements.define("date-countdown", DateCountdown);
}
